// FP 자원 간트 — 하단 상세탭 공용 **문맥 밴드**
//
// 하단 탭들은 "선택한 바 하나"에 대한 상세라, 조회 조건이 되는 값(공장·자원·스테이지·품목)이
// **전 행 동일**하다. 그런 값은 컬럼이 아니라 **이 그리드의 제목**이다 — 컬럼으로 두면
//   ① 트리로 접어도 그룹이 1개뿐이라 접기 가치가 0이고,
//   ② 트리든 병합이든 값이 첫 행에만 남아 **스크롤하면 문맥이 사라지며**,
//   ③ 가로 폭을 상수 몇 개가 차지한다.
// 밴드로 빼면 셋이 한 번에 풀린다. 경과·시안 비교는 reports/gantt-implementation.md §7-23,
// prototypes/gantt-detail-tab-layout.html.
//
// 항목 수가 탭마다 다르다(생산 계획 상세 = 공장·자원 2개 / PSI = 공장·스테이지·품목 3개)라
// `items` 배열로 받는다. 두 탭이 같은 조판을 쓰도록 여기 하나로 모았다 — 복제하면 한쪽만 조정된다.

import { Fragment } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";
import numberUtil from "@plannel/utils/number-util";

// 코드가 주(主)이고 명칭은 괄호로 덧붙인다(명칭이 없으면 코드만).
const withName = (code, name) => (code && name ? `${code} (${name})` : (code ?? name ?? ""));

const Label = ({ children }) => (
  <Typography sx={{ fontSize: 11.5, color: UI.headerFgDim, flex: "none" }}>{children}</Typography>
);

const Value = ({ children }) => (
  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.cellFg, flex: "none" }}>{children}</Typography>
);

const Sep = ({ children }) => (
  <Typography sx={{ fontSize: 12.5, color: UI.paneSepStrong, flex: "none" }}>{children}</Typography>
);

/**
 * items: [{ labelKey, code, name }] — 왼쪽부터 계층 순서(공장 › 스테이지 › 품목).
 * count: 행 수. 하단 상태바(Total Rows)를 없앤 자리를 대신한다(§7-23) — null이면 표시하지 않는다.
 */
const DetailContextBand = ({ items = [], count = null, t }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", gap: 1, flex: "0 0 auto",
      px: 1.25, py: 0.75, mb: 0.75,
      backgroundColor: UI.ctrlGroupBg, border: "1px solid", borderColor: UI.ctrlGroupLine, borderRadius: 1
    }}
  >
    {items.map((item, index) => (
      <Fragment key={item.labelKey}>
        {/* `›` = **계층**(공장 안의 스테이지 안의 품목). 아래 건수의 `·`와 구분한다. */}
        {index > 0 ? <Sep>›</Sep> : null}
        <Label>{t(item.labelKey)}</Label>
        <Value>{withName(item.code, item.name)}</Value>
      </Fragment>
    ))}

    {count == null ? null : (
      <>
        {/* 건수는 계층이 아니라 별도 항목이라 `·`를 쓴다 — `›`를 쓰면 "품목 아래 건수"로 읽힌다.
            **오른쪽 끝이 아니라 문맥 바로 옆**에 붙인다(2026-08-24 사용자 지적: 떨어져 있으면
            넓은 화면에서 시선이 닿지 않는다). */}
        <Sep>·</Sep>
        <Label>{t("count")}</Label>
        <Value>{numberUtil.formatNumber(count)}</Value>
      </>
    )}

    <Box sx={{ flex: "1 1 auto" }} />
  </Box>
);

export default DetailContextBand;
